import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Gate for authorized routes. Children render ONLY when the signed-in user is
 * authorized (provisioned + claims fresh, confirmed by /api/me). A signed-in but
 * not-authorized user is bounced home. Because `isAuthorized` flips true only
 * AFTER the first-login token refresh, no direct-Firestore screen renders before
 * the `audience` claim is in the token. UX gate only — the server re-enforces.
 */
export default function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading, isAuthorized } = useAuth();
  const location = useLocation();

  const spinner = (label: string) => (
    <div className="flex min-h-screen items-center justify-center bg-paper-50 text-ink-500 dark:bg-clay-floor dark:text-paper-200">
      <span className="animate-pulse">{label}</span>
    </div>
  );

  if (loading) return spinner('Loading…');
  if (!user) return <Navigate to="/" replace state={{ from: location.pathname }} />;
  if (isAuthorized === null) return spinner('Stepping into the akhara…');
  if (isAuthorized === false) return <Navigate to="/" replace />;
  return <>{children}</>;
}
